import browserStorage from '~/plugins/browserStorage';
import config from '~/plugins/config';
import { changeLocale } from './locale';
import { Locale } from './_types';
import i18nInstance from '.';
import { getTranslation } from '~/api/translations';

const createLocaleBrowserStorage = () => {
    const localeStorageKey = config.storage.data.locale.key;
    const { locale: configLocale, availableLocales } = config.app;

    const loadedLocales = ref<Locale[]>([]);
    const isLoadingLocale = ref<Locale>();

    /**
     * Cached locale
     * Must be ref - writable computed `activeLocale` does not work correctly (does not refresh getter when setting value)
     */
    const _cachedLocale: Ref<Locale | null> = ref(null);

    const localeDisplayNames = computed(() => {
        return new Intl.DisplayNames([activeLocale.value], {
            type: 'language',
        });
    });

    const activeLocale = computed({
        get: () => {
            if (_cachedLocale.value == null) {
                const locale = browserStorage.get<string>(localeStorageKey, configLocale);
                _cachedLocale.value = availableLocales.some((x) => x === locale) ? (locale as Locale) : configLocale;
            }

            return _cachedLocale.value;
        },
        set: (newLocale: Locale) => {
            _cachedLocale.value = newLocale;
            browserStorage.set(localeStorageKey, newLocale);
            changeLocale(newLocale);
            loadLocaleFromBE(newLocale);
        },
    });

    const init = async () => {
        const locale = activeLocale.value;
        changeLocale(locale);
        loadLocaleFromBE(locale);
    };

    const loadLocaleFromBE = async (locale: Locale, force = false) => {
        try {
            if (!config.app.beLocales) return;

            if (isLoadingLocale.value === locale || (loadedLocales.value.includes(locale) && !force)) {
                return true;
            }
            isLoadingLocale.value = locale;

            if (config.app.beLocales) {
                const beLocale = await getTranslation({ locale });

                if (!beLocale.isSuccess || beLocale.data === null) {
                    console.error(`[App/Locale | loadLocale] ${locale} failed, API error`);
                    return false;
                }

                // Merge local and remote messages
                i18nInstance.global.mergeLocaleMessage(locale, beLocale.data);
            }

            if (!loadedLocales.value.includes(locale)) {
                loadedLocales.value.push(locale);
            }

            return true;
        } finally {
            isLoadingLocale.value = undefined;
        }
    };

    return {
        localeDisplayNames,
        availableLocales,
        activeLocale,
        loadLocaleFromBE,
        init,
    };
};

const localeStorage = createLocaleBrowserStorage();

export default localeStorage;
